import { GridColDef } from '@mui/x-data-grid-pro';
import { StreetTurn } from '@api/Trinium/Endpoints/StreetTurn/types';
import RenderHeader from '@components/DataGrid/RenderHeader';
import StreetTurnMobileItem from './StreetTurnMobileItem';
import { getEnumValueByKey } from '@/Infrastructure/Utilities';
import { LegStatus, LegType, ParsedDate } from '@api/Trinium/types';
import RenderCell from '@components/DataGrid/RenderCell';
import StackedTextCell from '@components/DataGrid/ColumnDefinitions/StackedTextCell';
import DateSlot from '@components/DataGrid/DateSlot';
import Tag from '@components/Tag';

import styles from '@components/DataGrid/RenderCell.module.css';

export const streetTurnColumnDefinitions: GridColDef<StreetTurn>[] = [
	{
		field: 'orderNumber',
		minWidth: 80,
		width: 120,
		renderHeader: () => (
			<RenderHeader
				primaryText={'Order Number'}
				secondaryText={'Type, Leg'}
			/>
		),
		renderCell: ({ row }) => {
			const { jobId, jobCategoryTransportationType, csnNo, legNo, legType } = row;

			const fullOrderNumber = `${jobId.toString()}/${csnNo.toString().padStart(3, '0')}`;
			const legDisplay = `${String(legNo)}${legType ? ` - ${legType}` : ''}`;

			return (
				<RenderCell
					primarySlot={
						<div title={fullOrderNumber}>
							<span className={styles.bold}>{jobId}</span>
							<span className={styles['secondary-text']}>/{csnNo.toString().padStart(3, '0')}</span>
						</div>
					}
					secondarySlot={<span className={styles.bold}>{jobCategoryTransportationType.toUpperCase()}</span>}
					tertiarySlot={
						<span
							className={styles.bold}
							title={`Leg ${String(legNo)} - ${getEnumValueByKey(LegType, legType) ?? ''}`}
						>
							{legDisplay}
						</span>
					}
					disableIndentation
				/>
			);
		},
		disableReorder: true,
		getApplyQuickFilterFn: (value: string) => {
			return (_, row): boolean => {
				const { jobId, csnNo, jobCategoryTransportationType, legNo, legType } = row;
				return (
					caseInsensitiveIncludes(value, legNo) ||
					caseInsensitiveIncludes(value, legType) ||
					caseInsensitiveIncludes(value, [`${jobId.toString()}/${csnNo.toString().padStart(3, '0')}`, jobCategoryTransportationType])
				);
			};
		},
		valueGetter: (_, row: StreetTurn) => `${String(row.jobId)}|${String(row.legNo)}`,
		sortComparator: (v1, v2) => {
			if (typeof v1 === 'string' && typeof v2 === 'string') {
				const v1Split = v1.split('|');
				const v1OrderNo = v1Split[0];
				const v1LegNo = v1Split[1];

				const v2Split = v2.split('|');
				const v2OrderNo = v2Split[0];
				const v2LegNo = v2Split[1];

				if (Number(v1OrderNo) > Number(v2OrderNo)) {
					return 1;
				} else if (Number(v1OrderNo) < Number(v2OrderNo)) {
					return -1;
				}

				if (Number(v1LegNo) > Number(v2LegNo)) {
					return 1;
				} else if (Number(v1LegNo) < Number(v2LegNo)) {
					return -1;
				}
			}

			return 0;
		}
	},
	{
		field: 'legStatus',
		minWidth: 155,
		width: 155,
		renderHeader: () => (
			<RenderHeader
				primaryText={'Leg Info'}
				secondaryText={'Statuses'}
			/>
		),
		renderCell: ({ row }) => {
			const { legStatus, isLive, isHazmat, isOverweight, isBulk } = row;
			const status = getEnumValueByKey(LegStatus, legStatus);
			return (
				<RenderCell
					primarySlot={
						<Tag
							text={status}
							tagStyle={legStatus}
							align='left'
						/>
					}
					secondarySlot={
						<div className={styles['leg-icon-toolbar']}>
							<Tag
								text='LIVE'
								tagStyle={isLive ? 'live' : 'disabled'}
								dense
							/>
							<Tag
								text='HAZ'
								tagStyle={isHazmat ? 'haz' : 'disabled'}
								dense
							/>
							<Tag
								text='WGT'
								tagStyle={isOverweight ? 'wgt' : 'disabled'}
								dense
							/>
							<Tag
								text='BULK'
								tagStyle={isBulk ? 'bulk' : 'disabled'}
								dense
							/>
						</div>
					}
					disableIndentation
				/>
			);
		},
		getApplyQuickFilterFn: (value: string) => {
			return (_, row): boolean => {
				const { legStatus, isBulk, isHazmat, isLive, isOverweight } = row;
				return (
					(isBulk && caseInsensitiveIncludes(value, 'bulk')) ||
					(isHazmat && caseInsensitiveIncludes(value, 'hazmat')) ||
					(isOverweight && caseInsensitiveIncludes(value, 'overweight')) ||
					(isLive && caseInsensitiveIncludes(value, 'live')) ||
					caseInsensitiveIncludes(value, getEnumValueByKey(LegStatus, legStatus))
				);
			};
		}
	},
	{
		field: 'Driver',
		minWidth: 75,
		width: 175,
		renderHeader: () => (
			<RenderHeader
				primaryText='Driver'
				secondaryText='Truck'
			/>
		),
		renderCell: ({ row }) => (
			<StackedTextCell
				primaryText={row.drvName}
				secondaryText={row.tractorId?.toString()}
			/>
		),
		cellClassName: styles.cell,
		getApplyQuickFilterFn: (value: string) => {
			return (_, row): boolean => {
				const { drvName, tractorId } = row;
				return caseInsensitiveIncludes(value, [drvName, tractorId]);
			};
		},
		valueGetter: (_, row: StreetTurn) => `${row.drvName ?? ''} ${row.tractorId?.toString() ?? ''}`
	},
	{
		field: 'Container',
		width: 150,
		renderHeader: () => (
			<RenderHeader
				primaryText={'Container'}
				secondaryText={'Size, Type'}
			/>
		),
		renderCell: ({ row }) => (
			<StackedTextCell
				primaryText={row.cntrId}
				secondaryText={`${row.cntrSize} ${row.cntrType}`}
			/>
		),
		cellClassName: styles.cell,
		getApplyQuickFilterFn: (value: string) => {
			return (_, row): boolean => {
				const { cntrId, cntrSize, cntrType } = row;
				return caseInsensitiveIncludes(value, [cntrId, cntrSize, cntrType]);
			};
		},
		valueGetter: (_, row: StreetTurn) => [row.cntrId, row.cntrSize, row.cntrType].join(' ')
	},
	{
		field: 'cntrProviderName',
		renderHeader: () => <RenderHeader primaryText='CP' />,
		renderCell: ({ row }) => <StackedTextCell primaryText={row.cntrProviderName} />,
		getApplyQuickFilterFn: (value: string) => {
			return (_, row): boolean => {
				return caseInsensitiveIncludes(value, row.cntrProviderName);
			};
		}
	},
	{
		field: 'Order FCL',
		width: 150,
		renderHeader: () => (
			<RenderHeader
				primaryText={'Order FCL'}
				secondaryText={'Schedule Delivery, Leg'}
			/>
		),
		renderCell: ({ row }) => (
			<StackedTextCell
				primaryText={row.fclDeliveryDate?.dateTime}
				secondaryText={`${String(row.fclLegNo)}${row.fclLegType ? ` - ${row.fclLegType}` : ''}`}
			/>
		),
		cellClassName: styles.cell,
		getApplyQuickFilterFn: (value: string) => {
			return (_, row): boolean => {
				const { fclLegNo, fclLegType } = row;
				return caseInsensitiveIncludes(value, [fclLegNo, fclLegType]);
			};
		},
		valueGetter: (_, row: StreetTurn) => row.fclDeliveryDate,
		sortComparator: (v1: ParsedDate | null, v2: ParsedDate | null) => {
			if (!v1) {
				return 1;
			}
			if (!v2) {
				return -1;
			}

			return v1.unix - v2.unix;
		}
	},
	{
		field: 'Scheduled Delivery',
		width: 150,
		renderHeader: () => <RenderHeader primaryText={'Scheduled Delivery'} />,
		renderCell: ({ row }) => (
			<RenderCell
				primarySlot={
					<DateSlot
						fromDate={row.scheduledDeliveryDateFrom}
						toDate={row.scheduledDeliveryDateTo}
						usePastDueStyle
					/>
				}
			/>
		),
		cellClassName: styles.cell,
		valueGetter: (_, row: StreetTurn) => row.scheduledDeliveryDateFrom,
		sortComparator: (v1: ParsedDate | null, v2: ParsedDate | null) => {
			if (!v1) {
				return 1;
			}
			if (!v2) {
				return -1;
			}
			return v1.unix - v2.unix;
		}
	},
	{
		field: 'Pickup',
		width: 150,
		renderHeader: () => (
			<RenderHeader
				primaryText={'Pickup'}
				secondaryText={'City, State'}
			/>
		),
		renderCell: ({ row }) => (
			<StackedTextCell
				primaryText={row.puName}
				secondaryText={`${row.puCity ?? ''}, ${row.puState ?? ''}`}
			/>
		),
		cellClassName: styles.cell,
		getApplyQuickFilterFn: (value: string) => {
			return (_, row): boolean => {
				const { puName, puCity, puState } = row;
				return caseInsensitiveIncludes(value, [puName, puCity, puState]);
			};
		},
		valueGetter: (_, row: StreetTurn) => [row.puName, row.puCity, row.puState].join(' ')
	}
];

export const mobileStreetTurnColumnDefs: GridColDef<StreetTurn>[] = [
	{
		field: 'Mobile Card',
		flex: 1,
		renderCell: ({ row }) => <StreetTurnMobileItem streetTurn={row} />,
		getApplyQuickFilterFn: (value: string) => {
			return (_, row): boolean => {
				const searchableFields = [
					row.jobId,
					row.jobCategoryTransportationType,
					row.csnNo,
					row.legNo,
					row.legType,
					row.legStatus,
					row.drvName,
					row.tractorId,
					row.cntrId,
					row.cntrSize,
					row.cntrType,
					row.cntrProviderName,
					row.fclLegNo,
					row.fclLegType,
					row.puName,
					row.puCity,
					row.puState
				];
				return caseInsensitiveIncludes(value, searchableFields);
			};
		}
	}
];

export function caseInsensitiveIncludes<T>(includesValue: string, obj: T | T[]): boolean {
	switch (typeof obj) {
		case 'object': {
			if (!obj) return false;
			const obj2 = obj as Record<string, unknown>;
			return Object.keys(obj2).some((key) => caseInsensitiveIncludes(includesValue, obj2[key]));
		}

		case 'string':
			return obj.toLowerCase().includes(includesValue.toLowerCase());

		case 'number':
			return obj.toString().includes(includesValue);

		case 'undefined':
			return false;

		default:
			throw new Error(`Unsupported type ${typeof obj} could not be processed in caseInsensitiveIncludes method.`);
	}
}
